import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  digitalOperationsTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { formatCustomerReferenceFromPlatform } from './catalog-customer.util';
import { CatalogOffering, CatalogService, DigitalEntitlement } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-digital-entitlements',
  imports: [
    AinPageComponent, AinStateComponent, TableModule, ButtonModule, DialogModule, FormsModule,
    InputTextModule, SelectModule, CheckboxModule, CustomerSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.digital.entitlements.title')" [description]="t('catalog.digital.entitlements.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.digital.entitlements.provision')" (click)="openProvision()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>
          @if (message()) { <p class="message">{{ message() }}</p> }
          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.digital.entitlements.offeringId') }}</th>
                <th>{{ t('catalog.digital.entitlements.customer') }}</th>
                <th>{{ t('catalog.digital.entitlements.credits') }}</th>
                <th>{{ t('catalog.digital.entitlements.saleAmount') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.status }}</td>
                <td>{{ row.offeringId }}</td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.customerReference || row.id }}</button></td>
                <td>{{ row.remainingUsageCredits }} / {{ row.usageCreditsGranted }}</td>
                <td>{{ row.saleAmount }} {{ row.currencyCode }}</td>
                <td>
                  <button pButton type="button" size="small" severity="secondary"
                    [label]="t('catalog.digital.entitlements.openLicenseCenter')"
                    (click)="openDetail(row.id)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog [header]="t('catalog.digital.entitlements.provision')" [(visible)]="provisionVisible" [modal]="true" [style]="{ width: '460px' }">
        <div class="form">
          <label>{{ t('catalog.digital.entitlements.offeringId') }}</label>
          <p-select [options]="digitalOfferingOptions()" optionLabel="label" optionValue="value" [(ngModel)]="provisionOfferingId" [style]="{ width: '100%' }" />
          <label>{{ t('catalog.digital.entitlements.customer') }}</label>
          <ain-customer-selector [(ngModel)]="provisionCustomerId" />
          <label>{{ t('catalog.digital.entitlements.saleAmount') }}</label>
          <input pInputText type="number" [(ngModel)]="provisionAmount" class="full" />
          <label>{{ t('catalog.digital.entitlements.credits') }}</label>
          <input pInputText type="number" [(ngModel)]="provisionCredits" class="full" />
          <div class="check-row">
            <p-checkbox [(ngModel)]="provisionPostFinancial" [binary]="true" inputId="postDig" />
            <label for="postDig">{{ t('catalog.digital.entitlements.postFinancial') }}</label>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="provisionVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.digital.entitlements.provision')" [disabled]="acting()" (click)="submitProvision()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .live-status { font-size: 0.8rem; color: #6b7280; }
    .live-status.connected { color: #166534; }
    .message { margin: 0; color: #166534; font-weight: 600; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .form { display: grid; gap: 8px; }
    .full { width: 100%; }
    .check-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class DigitalEntitlementsComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly rows = signal<DigitalEntitlement[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly message = signal('');

  provisionVisible = false;
  provisionOfferingId = '';
  provisionCustomerId: string | null = null;
  provisionAmount = 100;
  provisionCredits = 100;
  provisionPostFinancial = false;

  private liveEventsSubscription?: Subscription;
  private subscribedTopic = '';
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.digitalOperationsChanged))
      .subscribe(() => this.load(false));

    this.service.listOfferings().subscribe({
      next: rows => this.offerings.set(rows.filter(o => o.digitalDeliveryEnabled))
    });
    this.load();
    void this.subscribeToTopic();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  digitalOfferingOptions(): { label: string; value: string }[] {
    return this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listDigitalEntitlements().subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  openProvision(): void {
    this.provisionOfferingId = this.offerings()[0]?.id ?? '';
    this.provisionCustomerId = null;
    this.provisionAmount = 100;
    this.provisionCredits = 100;
    this.provisionVisible = true;
  }

  submitProvision(): void {
    if (!this.provisionOfferingId || !this.provisionCustomerId) {
      this.message.set(this.t('catalog.customer.required'));
      return;
    }

    this.acting.set(true);
    const customerId = this.provisionCustomerId;
    this.financeApi.getPlatformCustomer(customerId).subscribe({
      next: customer => {
        this.service.provisionDigitalEntitlement({
          offeringId: this.provisionOfferingId,
          customerId,
          customerReference: formatCustomerReferenceFromPlatform(customer),
          saleAmount: this.provisionAmount,
          usageCreditsGranted: this.provisionCredits,
          postFinancialEntry: this.provisionPostFinancial
        }).subscribe({
          next: row => {
            this.provisionVisible = false;
            this.message.set(this.t('catalog.digital.entitlements.provisioned'));
            this.load();
            this.acting.set(false);
            void this.router.navigate(['/digital/entitlements', row.id]);
          },
          error: () => this.acting.set(false)
        });
      },
      error: () => this.acting.set(false)
    });
  }

  openDetail(id: string): void { void this.router.navigate(['/digital/entitlements', id]); }

  private async subscribeToTopic(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;
    this.subscribedTopic = digitalOperationsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }

  t(key: string): string { return this.localization.translate(key); }
}
